import { useState, useEffect } from "react";
import { Modal, Form, Select, InputNumber, Button } from "antd";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { createHourlyRate } from "../../api/teachers";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { toast } from "sonner";
import withPermission from "../../core/common/autorisations/withPermission";

const { Option } = Select;

interface Teacher {
  id: string;
  firstname: string;
  lastname: string;
  matricule?: string;
}

interface CreateHourlyRateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  teachersWithoutRate: Teacher[];
}

const CreateHourlyRateTeacherModal = ({
  visible,
  onCancel,
  onSuccess,
  teachersWithoutRate,
}: CreateHourlyRateModalProps) => {
  const [form] = Form.useForm();
  const { selectedYear } = useAcademicYear();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async (values: any) => {
    if (!selectedYear?.id) {
      toast.error("Veuillez sélectionner une année académique");
      return;
    }
    // console.log("values: ", values);
    setLoading(true);
    try {
      await createHourlyRate(
        {
          id_teacherProfil: values.teacherId,
          id_academicYear: selectedYear.id,
          amount: values.amount,
        },
        buildPermissionContext(Resource.HOURLYRATE_TEACHER, Action.CREATE),
      );

      toast.success("Taux horaire créé avec succès");
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      // console.error("Erreur lors de la création du taux horaire:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produite lors de la création du taux horaire",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Ajouter un taux horaire"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          amount: 0,
        }}
      >
        <Form.Item
          name="teacherId"
          label="Enseignant"
          rules={[
            { required: true, message: "Veuillez sélectionner un enseignant" },
          ]}
        >
          <Select
            placeholder="Sélectionner un enseignant"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children
                ?.toString()
                ?.toLowerCase()
                ?.includes(input.toLowerCase()) ?? false
            }
          >
            {teachersWithoutRate.map((teacher) => (
              <Option key={teacher.id} value={teacher.id}>
                {`${teacher.firstname} ${teacher.lastname}${teacher.matricule ? ` (${teacher.matricule})` : ""}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="amount"
          label="Montant du taux horaire (FCFA)"
          rules={[
            {
              required: true,
              message: "Veuillez saisir le montant du taux horaire",
            },
            {
              type: "number",
              min: 0,
              message: "Le montant doit être positif",
            },
          ]}
        >
          <InputNumber
            placeholder="Entrez le montant"
            style={{ width: "100%" }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
            }
            parser={(value: string | undefined): number => {
              const parsed = Number(value?.replace(/\s/g, "") || "0");
              return isNaN(parsed) ? 0 : parsed;
            }}
            min={0}
            step={1000}
          />
        </Form.Item>

        <Form.Item>
          <div className="d-flex justify-content-end gap-2">
            <Button onClick={onCancel}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Créer le taux horaire
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default withPermission(
  CreateHourlyRateTeacherModal,
  [buildPermission(Resource.HOURLYRATE_TEACHER, Action.CREATE)],
  "masquer",
);
